/**
 * CrisisBanner Component
 *
 * Persistent, subtle banner that appears at the bottom of screens when:
 * 1. Crisis detection triggers (safety.showResources = true in API response)
 * 2. User taps "Need help?" link anywhere in the app
 *
 * Design Principles:
 * - NON-BLOCKING: Analysis always completes, resources shown alongside
 * - SUBTLE: Collapsed state is unobtrusive
 * - ACCESSIBLE: WCAG AAA compliant, tap-to-call support
 * - PRIVACY-RESPECTING: No message content logged
 *
 * Accessibility:
 * - 48px minimum touch targets (exceeds 44px requirement)
 * - High contrast colors (7:1+ ratio)
 * - Screen reader labels
 * - Semantic roles
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Week 1)
 */

import { View, Text, Pressable, Platform, StyleSheet } from 'react-native';
import { useState, useEffect } from 'react';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';
import { CrisisResourcesModal } from './CrisisResourcesModal';
import { CrisisFeedbackBottomSheet } from './CrisisFeedbackBottomSheet';
import { useSafetyModals } from '../../contexts/SafetyModalsContext';

export interface CrisisBannerProps {
  /**
   * Whether crisis was detected in the analysis
   * Triggers automatic expansion of banner
   */
  crisisDetected?: boolean;

  /**
   * Severity level from crisis detection
   * Affects styling urgency
   */
  severity?: 'low' | 'medium' | 'high' | 'critical';

  /**
   * Screen where banner is displayed (for analytics)
   */
  screen?: 'home' | 'results' | 'history';

  /**
   * Moderation event ID for practitioner/feedback flows
   * Enables "Get Help" button to connect with practitioners
   */
  moderationEventId?: string;

  /**
   * Optional callback when resources are accessed
   */
  onResourceAccessed?: (resourceType: string) => void;

  /**
   * Test ID for E2E testing
   */
  testID?: string;
}

/**
 * CrisisBanner - Persistent help banner with expandable resources modal
 *
 * States:
 * - Collapsed (default): Subtle "Need help? Get support" text
 * - Expanded: Opens CrisisResourcesModal with full resources
 * - Triggered: Auto-expands when crisisDetected=true
 */
export function CrisisBanner({
  crisisDetected = false,
  severity = 'low',
  screen = 'home',
  moderationEventId,
  onResourceAccessed,
  testID,
}: CrisisBannerProps) {
  const { t } = useTranslation('safety');
  const { triggerPractitionerPrompt } = useSafetyModals();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFeedbackSheetVisible, setIsFeedbackSheetVisible] = useState(false);
  const [hasTrackedShow, setHasTrackedShow] = useState(false);

  // Auto-show modal when crisis is detected (high/critical severity)
  useEffect(() => {
    if (crisisDetected && (severity === 'high' || severity === 'critical')) {
      setIsModalVisible(true);

      // Track auto-triggered show
      captureEvent('Safety_ResourcesShown', {
        triggered_by: 'crisis_detection',
        severity,
        screen,
      });
    }
  }, [crisisDetected, severity, screen]);

  // Track banner visibility
  useEffect(() => {
    if (!hasTrackedShow) {
      captureEvent('Safety_BannerShown', {
        screen,
        crisis_detected: crisisDetected,
        severity: crisisDetected ? severity : undefined,
      });
      setHasTrackedShow(true);
    }
  }, [hasTrackedShow, screen, crisisDetected, severity]);

  const handleBannerPress = () => {
    setIsModalVisible(true);

    captureEvent('Safety_BannerClicked', {
      screen,
      crisis_detected: crisisDetected,
    });
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleResourceAccessed = (resourceType: string) => {
    captureEvent('Safety_ResourceClicked', {
      resource_type: resourceType,
      screen,
      triggered_by: crisisDetected ? 'crisis_detection' : 'user_initiated',
    });

    onResourceAccessed?.(resourceType);
  };

  const handleGetHelpPress = () => {
    triggerPractitionerPrompt({
      moderationEventId,
      severity,
    });

    captureEvent('Safety_GetHelpClicked', {
      screen,
      severity,
      has_moderation_event: !!moderationEventId,
    });
  };

  const handleFeedbackTriggerPress = () => {
    setIsFeedbackSheetVisible(true);

    captureEvent('Crisis_FeedbackTrigger_Clicked', {
      screen,
      severity,
      moderation_event_id: moderationEventId,
    });
  };

  const handleFeedbackSheetClose = () => {
    setIsFeedbackSheetVisible(false);
  };

  const handleFeedbackSubmitted = () => {
    setIsFeedbackSheetVisible(false);
    // Analytics tracked by the bottom sheet component
  };

  // Determine banner styling based on crisis state
  const bannerStyle = crisisDetected
    ? styles.bannerTriggered
    : styles.bannerDefault;

  const textStyle = crisisDetected
    ? styles.textTriggered
    : styles.textDefault;

  return (
    <>
      {/* Collapsed Banner */}
      <Pressable
        onPress={handleBannerPress}
        style={[styles.banner, bannerStyle]}
        accessibilityRole="button"
        accessibilityLabel={t('banner.accessibility_label')}
        accessibilityHint={t('banner.accessibility_hint')}
        testID={testID || 'crisis-banner'}
      >
        <View style={styles.bannerContent}>
          {/* Heart icon */}
          <Text style={styles.icon}>💙</Text>

          {/* Main text */}
          <Text style={[styles.text, textStyle]}>
            {crisisDetected
              ? t('banner.text_triggered')
              : t('banner.text_default')}
          </Text>

          {/* Arrow indicator */}
          <Text style={[styles.arrow, textStyle]}>→</Text>
        </View>
      </Pressable>

      {/* Get Help Button - Only show when crisis detected with moderationEventId */}
      {crisisDetected && moderationEventId && (
        <Pressable
          onPress={handleGetHelpPress}
          style={styles.getHelpButton}
          accessibilityRole="button"
          accessibilityLabel={t('banner.get_help_accessibility_label')}
          accessibilityHint={t('banner.get_help_accessibility_hint')}
          testID="crisis-get-help-button"
        >
          <Text style={styles.getHelpText}>{t('banner.get_help_button')}</Text>
        </Pressable>
      )}

      {/* Inline Feedback Trigger - Only show when crisis detected with moderationEventId */}
      {crisisDetected && moderationEventId && (
        <Pressable
          onPress={handleFeedbackTriggerPress}
          style={styles.feedbackTrigger}
          accessibilityRole="button"
          accessibilityLabel={t('detection_feedback.inline_trigger_accessibility')}
          accessibilityHint={t('detection_feedback.inline_trigger_hint')}
          testID="crisis-feedback-trigger"
        >
          <Text style={styles.feedbackTriggerText}>
            {t('detection_feedback.inline_trigger')}
          </Text>
        </Pressable>
      )}

      {/* Resources Modal */}
      <CrisisResourcesModal
        visible={isModalVisible}
        severity={severity}
        crisisTriggered={crisisDetected}
        onClose={handleModalClose}
        onResourceAccessed={handleResourceAccessed}
      />

      {/* Feedback Bottom Sheet */}
      <CrisisFeedbackBottomSheet
        visible={isFeedbackSheetVisible}
        moderationEventId={moderationEventId}
        severity={severity}
        onClose={handleFeedbackSheetClose}
        onSubmitted={handleFeedbackSubmitted}
      />
    </>
  );
}

const styles = StyleSheet.create({
  banner: {
    width: '100%',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginVertical: 8,
    minHeight: 48, // Accessibility: 48px minimum touch target
  },
  bannerDefault: {
    backgroundColor: '#EFF6FF', // blue-50
    borderWidth: 1,
    borderColor: '#BFDBFE', // blue-200
  },
  bannerTriggered: {
    backgroundColor: '#FEF2F2', // red-50
    borderWidth: 1,
    borderColor: '#FECACA', // red-200
  },
  bannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  icon: {
    fontSize: 16,
  },
  text: {
    fontSize: 14,
    fontWeight: '500',
  },
  textDefault: {
    color: '#1E40AF', // blue-800
  },
  textTriggered: {
    color: '#991B1B', // red-800
    fontWeight: '600',
  },
  arrow: {
    fontSize: 14,
    fontWeight: '600',
  },
  getHelpButton: {
    width: '100%',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    marginTop: 8,
    backgroundColor: '#7C3AED', // violet-600 - supportive, professional
    minHeight: 48, // Accessibility: 48px minimum touch target
    alignItems: 'center',
    justifyContent: 'center',
  },
  getHelpText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  feedbackTrigger: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    marginTop: 4,
    minHeight: 44, // Accessibility: minimum touch target
  },
  feedbackTriggerText: {
    color: '#6B7280', // gray-500 - subtle, non-intrusive
    fontSize: 13,
    textDecorationLine: 'underline',
  },
});

export default CrisisBanner;
